import React, { useEffect, useState, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedProvincesState,
  selectedPemdaState,
  sidebarisexpandAtom,
  selectedJenisTkdState,
  months,
  startMonthAtom,
  endMonthAtom,
} from "states/recoil";
import {
  Center,
  Spinner,
  Text,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Flex,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import {
  HiChartBar,
  HiDotsHorizontal,
  HiOutlineDownload,
  HiArrowsExpand,
} from "react-icons/hi";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { DownloadImage } from "components/functions/DownloadImage";
import { ExportToExcel } from "components/functions/ExportToExcel";
import { ToggleFullScreen } from "components/functions/ToggleFullscreen";
import { UseTogglePopUp } from "components/functions/UseTogglePopUp";
import { tahunAnggaranAtom } from "states/recoil";

function VerticalBarLineChart({ data, xaxisData, titleChart }) {
  //ref to downloadImage
  const chartContainerRef = useRef(null);

  // recoil state
  const sidebarisexpandGet = useRecoilValue(sidebarisexpandAtom);
  const [selectedProvinces] = useRecoilState(selectedProvincesState);
  const [selectedPemda] = useRecoilState(selectedPemdaState);
  const [selectedJenisTKD] = useRecoilState(selectedJenisTkdState);
  const monthArray = useRecoilValue(months);
  const [selectedStartMonth] = useRecoilState(startMonthAtom);
  const [selectedEndMonth] = useRecoilState(endMonthAtom);

  // local state
  const [isLoading, setIsLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { isOpen, openPopup, closePopup } = UseTogglePopUp();
  const [exportedData, setExportedData] = useState({
    categories: [],
    series: [],
  });

  // chart data state
  const [chartData, setChartData] = useState([]);

  // get the data
  useEffect(() => {
    setRawData(data);
  }, [data]);

  // process chart data
  useEffect(() => {
    setIsLoading(true);

    if (
      !rawData.length ||
      !selectedProvinces.length ||
      !selectedPemda.length ||
      !selectedJenisTKD.length
    ) {
      setChartData([]);
      setIsLoading(false);
      return;
    }

    const startMonthIndex = monthArray.indexOf(selectedStartMonth) + 1;
    const endMonthIndex = monthArray.indexOf(selectedEndMonth) + 1;

    const filteredData = rawData.filter(
      (row) =>
        selectedProvinces.includes(row["NM PROV"]) &&
        selectedPemda.includes(row["NM PEMDA"]) &&
        selectedJenisTKD.includes(row["JENIS TKD"])
    );

    const grouped = {};
    filteredData.forEach((row) => {
      const jenis = row[xaxisData];
      if (!jenis) return;

      if (!grouped[jenis]) {
        grouped[jenis] = {
          "PAGU ALOKASI": 0,
          REALISASI: 0,
        };
      }

      let sumRealisasiSelectedMonths = 0;
      for (
        let monthIdx = startMonthIndex;
        monthIdx <= endMonthIndex;
        monthIdx++
      ) {
        const key = `REALISASI ${monthIdx.toString().padStart(2, "0")}`;
        sumRealisasiSelectedMonths += parseFloat(row[key] || 0);
      }

      grouped[jenis]["PAGU ALOKASI"] += parseFloat(row["PAGU ALOKASI"] || 0);
      grouped[jenis]["REALISASI"] += sumRealisasiSelectedMonths;
    });

    const processedData = Object.keys(grouped).map((key) => {
      const pagu = grouped[key]["PAGU ALOKASI"];
      const realisasi = grouped[key]["REALISASI"];
      const percentage = pagu ? (realisasi / pagu) * 100 : 0;

      return {
        category: key,
        pagu: pagu / 1_000000_000,
        realisasi: realisasi / 1_000_000_000,
        percentage: percentage,
        rawPagu: pagu,
        rawRealisasi: realisasi,
      };
    });
    setChartData(processedData);

    setIsLoading(false);
  }, [
    rawData,
    selectedProvinces,
    selectedPemda,
    selectedJenisTKD,
    xaxisData,
    selectedEndMonth,
    selectedStartMonth,
    tahunAnggaranAtom,
  ]);

  //update exportdata
  useEffect(() => {
    setExportedData({
      categories: chartData.map((item) => item.category),
      series: [
        {
          name: "Pagu Alokasi",
          data: chartData.map((item) => item.pagu * 1_000000_000),
        },
        {
          name: "Realisasi",
          data: chartData.map((item) => item.realisasi * 1_000000_000),
        },
        { name: "Persentase", data: chartData.map((item) => item.percentage) },
      ],
    });
  }, [chartData]);

  // formatters
  const formatCurrency = (val) =>
    val?.toLocaleString("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    });

  const formatMiliar = (val) => `${Math.round(val)} M`;

  const formatPercentage = (val) => `${val.toFixed(2)} %`;

  // Calculate leftMaxValue safely
  const leftMaxValue =
    chartData.reduce((max, d) => Math.max(max, d.pagu), 0) * 1.1;

  let leftTicks = [];
  if (leftMaxValue > 0 && Number.isFinite(leftMaxValue)) {
    const step = leftMaxValue / 5;
    for (let i = 0; i <= 5; i++) {
      leftTicks.push(step * i);
    }
  } else {
    leftTicks = [0, 1];
  }

  return (
    <>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex align="center" gap={4}>
          <IconButton icon={<HiChartBar />} aria-label="chart" size="sm" />
          <Heading size="sm">{titleChart}</Heading>
        </Flex>
        <Flex gap={4}>
          <IconButton
            icon={<HiArrowsExpand />}
            aria-label="export"
            size="sm"
            onClick={openPopup}
          />
          <Menu>
            <MenuButton as={Button} size="sm">
              <HiOutlineDownload />
            </MenuButton>
            <MenuList
              maxHeight="300px"
              overflowY="auto"
              minWidth="250px"
              zIndex={3}
            >
              <MenuItem
                onClick={() =>
                  DownloadImage(chartContainerRef.current, titleChart)
                }
                title={titleChart}
              >
                Unduh sebagai gambar (.PNG)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  ExportToExcel(exportedData, `${titleChart}.xlsx`, false)
                }
              >
                Unduh sebagai spreadsheet (.xlsx)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody ref={chartContainerRef}>
        {isLoading ? (
          <Center w="100%" h="500px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        ) : chartData.length === 0 ? (
          <Center w="100%" h="500px">
            <Text>Tidak ada data yang ditampilkan.</Text>
          </Center>
        ) : (
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 50, left: 20, bottom: 50 }}
              barCategoryGap="150px"
            >
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis
                dataKey="category"
                angle={-45}
                textAnchor="end"
                interval={0}
                fontSize={10}
                height={100}
              />
              <YAxis
                yAxisId="left"
                ticks={leftTicks}
                tickFormatter={formatMiliar}
                domain={[0, leftMaxValue]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]} // fixed 100% max on right axis
                tickFormatter={(val) => `${val.toFixed(0)}%`}
              />
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === "Persentase (%)") {
                    return [formatPercentage(value), name];
                  } else if (name === "PAGU ALOKASI") {
                    return [formatCurrency(props.payload.rawPagu), name];
                  } else if (name === "REALISASI") {
                    return [formatCurrency(props.payload.rawRealisasi), name];
                  }
                  if (name.startsWith("Persentase")) {
                    return [`${(value / 10).toFixed(2)} %`, name];
                  }

                  return value;
                }}
              />
              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{
                  paddingTop: 0,
                  paddingBottom: 20,
                  fontSize: 12,
                }}
              />

              <Bar
                yAxisId="left"
                dataKey="pagu"
                name="PAGU ALOKASI"
                fill="#005599"
                barSize={40}
              />
              <Bar
                yAxisId="left"
                dataKey="realisasi"
                name="REALISASI"
                fill="#00E396"
                barSize={40}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="percentage"
                name="Persentase (%)"
                stroke="#FEB019"
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 8 }}
                data={chartData.map((item) => ({
                  ...item,
                  scaledPercentage:
                    (item.percentage / 100) *
                    chartData.reduce((max, d) => Math.max(max, d.pagu), 0) *
                    1.2,
                }))}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardBody>

      {isOpen && (
        <ToggleFullScreen onClose={closePopup} rawData={rawData}>
          {/* Render the expanded chart here */}
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isLoading ? (
              <Center w="100%" h="500px">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : rawData.length === 0 ? (
              <Center w="100%" h="500px">
                <Text>Tidak ada data yang ditampilkan.</Text>
              </Center>
            ) : (
              <Center w="100%" h="100%">
                <ResponsiveContainer width="100%" height={500}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 20, right: 50, left: 20, bottom: 50 }}
                    barCategoryGap="150px"
                  >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis
                      dataKey="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      fontSize={10}
                      height={100}
                    />
                    <YAxis
                      yAxisId="left"
                      ticks={leftTicks}
                      tickFormatter={formatMiliar}
                      domain={[0, leftMaxValue]}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 100]} // fixed 100% max on right axis
                      tickFormatter={(val) => `${val.toFixed(0)}%`}
                    />
                    <Tooltip
                      formatter={(value, name, props) => {
                        if (name === "Persentase (%)") {
                          return [formatPercentage(value), name];
                        } else if (name === "PAGU ALOKASI") {
                          return [formatCurrency(props.payload.rawPagu), name];
                        } else if (name === "REALISASI") {
                          return [
                            formatCurrency(props.payload.rawRealisasi),
                            name,
                          ];
                        }
                        if (name.startsWith("Persentase")) {
                          return [`${(value / 10).toFixed(2)} %`, name];
                        }

                        return value;
                      }}
                    />
                    <Legend
                      verticalAlign="top"
                      align="center"
                      wrapperStyle={{
                        paddingTop: 0,
                        paddingBottom: 20,
                        fontSize: 12,
                      }}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="pagu"
                      name="PAGU ALOKASI"
                      fill="#005599"
                      barSize={40}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="realisasi"
                      name="REALISASI"
                      fill="#00E396"
                      barSize={40}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="percentage"
                      name="Persentase (%)"
                      stroke="#FEB019"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 8 }}
                      data={chartData.map((item) => ({
                        ...item,
                        scaledPercentage:
                          (item.percentage / 100) *
                          chartData.reduce(
                            (max, d) => Math.max(max, d.pagu),
                            0
                          ) *
                          1.2,
                      }))}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Center>
            )}
          </div>
        </ToggleFullScreen>
      )}
    </>
  );
}

export default VerticalBarLineChart;
